import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown, MapPin } from "lucide-react";
import { copy } from "@/lib/copy";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { useCms } from "@/lib/useCms";
import { mapArea } from "@/lib/cmsAdapters";
import FitText from "@/components/ui/FitText";

// The whole site runs dark/cyan now (Home and every other page), so the nav
// no longer needs a separate light-theme palette - only Home still gets the
// transparent-over-hero floating treatment, since it's the only page that
// opens on a full-bleed image instead of flat dark content.
// CSS variable (Admin > Appearance > Primary Color), hex is the fallback
// used until ThemeProvider has fetched a value - see index.css/ThemeProvider.jsx.
const CYAN = "var(--horizon-primary, #22d3ee)";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  // The nav is dark year-round, so it wants the light-on-dark logo variant
  // first, falling back to the general logo, then the static asset in
  // /public if nothing's been uploaded in Admin > Website Settings yet.
  const { logoLightUrl, logoUrl, name: bizName } = useSiteSettings();
  const headerLogo = logoLightUrl || logoUrl || "/logo-hsec.png";
  // Full real business name (Admin > Website Settings > Business Name) -
  // always shown in full, however long the admin makes it. "Solar" is
  // picked out in cyan wherever it appears, same visual treatment the old
  // hardcoded "Horizon Solar" always had, just computed instead of
  // assumed. This used to swap to a separate "Short Brand Name" below a
  // measured width threshold, so a long full name never collided with the
  // nav links beside it - now FitText (below) handles that instead, by
  // shrinking the name's own rendered size to whatever room it actually
  // has rather than substituting different text, so the admin-entered
  // name always genuinely appears.
  const highlightSolar = (str) => {
    const i = str.indexOf("Solar");
    if (i === -1) return str;
    return (
      <>
        {str.slice(0, i)}
        <span style={{ color: CYAN }}>Solar</span>
        {str.slice(i + "Solar".length)}
      </>
    );
  };
  const wordmark = highlightSolar(bizName);
  // Areas dropdown - real Admin > Service Areas data (active areas only,
  // the public API already filters to those), falling back to the static
  // list only while the API is loading or unreachable. The dropdown has
  // room for a handful of areas, not the full list, so it's capped the
  // same way AreaDetailPage's own "popular suburbs" picker is - the
  // ServiceArea model has no separate "featured" flag, just displayOrder.
  const { data: apiAreas } = useCms("/api/areas", null);
  const navAreas = (apiAreas?.length ? apiAreas.map(mapArea) : (copy.areas?.featured || [])).slice(0, 8);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  let timeoutId;

  // Home's hero runs for several viewport-heights, so a fixed scrollY
  // threshold either flips the nav to "solid" almost immediately (killing
  // the transparent-over-photo look for ~99% of the hero) or leaves it
  // transparent long after the hero photo has ended (illegible over dark
  // page content). Watching where the actual end of the hero (#hero-end)
  // sits relative to the floating navbar gets this right regardless of
  // hero length.
  //
  // This used to be an IntersectionObserver with a negative rootMargin
  // meant to shrink the observed area down to a thin strip at the very top
  // - but rootMargin's top value shrinks the root *inward from the top*,
  // it doesn't carve out a strip *at* the top, so the "observed" region was
  // actually most of the viewport (64px down to the bottom). Since the hero
  // is 92vh, #hero-end sits inside that region on the very first paint,
  // before any scrolling - so `pastHero` came out `true` immediately on
  // every load, which is exactly the "navbar looks solid over the hero
  // photo" behaviour reported. A direct geometric check against the
  // marker's own position is simpler and doesn't have that failure mode.
  useEffect(() => {
    if (!isHome) {
      setPastHero(false);
      return undefined;
    }
    const marker = document.getElementById("hero-end");
    if (!marker) return undefined;
    const NAV_THRESHOLD = 80; // px - roughly the floating navbar's own height + top inset
    let raf = null;
    const update = () => {
      raf = null;
      setPastHero(marker.getBoundingClientRect().top <= NAV_THRESHOLD);
    };
    const onScrollOrResize = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isHome]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutId);
    setIsAreasOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setIsAreasOpen(false);
    }, 150);
  };

  const linkCls = "font-medium text-sm tracking-wide text-white/70 hover:text-white transition-colors whitespace-nowrap";

  // On the hero, only the image mark itself floats free and bigger - the
  // dot and "Horizon Solar" wordmark stay put in the nav capsule at all
  // times, per feedback that pulling the whole lockup out read as
  // disconnected from the bar. The image is anchored to the capsule
  // itself (a negative-margin overlap below, not a separate stacked block
  // with a gap) so it reads as one badge sitting on the bar rather than
  // two unrelated rows. The moment pastHero flips (same #hero-end marker
  // the capsule's own transparent->solid switch already uses, so both
  // happen at the same scroll position), the big image unmounts and the
  // small inline one - sharing the same layoutId - mounts in its normal
  // place next to the wordmark. Framer Motion animates the FLIP
  // automatically: same identity, different size and position, so it
  // reads as the mark shrinking and gliding into the bar.
  const showBigHeroLogo = isHome && !pastHero;
  const logoImg = (size) => (
    <motion.img
      layoutId="horizon-logo-img"
      src={headerLogo}
      alt=""
      // The hero-state logo scales continuously with viewport width
      // (clamp, not a fixed breakpoint size) so it keeps growing on very
      // wide/high-resolution displays (4K/5K monitors, iMacs) instead of
      // plateauing at whatever the last defined Tailwind breakpoint
      // happened to be - a fixed sm:/lg: size looks progressively smaller
      // relative to the page the wider the real viewport gets past that
      // last breakpoint. Kept in exact lockstep with the matching
      // clearance padding in HeroCarousel.jsx's slide layouts (same 4vw
      // slope, offset by a constant margin) so a bigger logo can never
      // catch up to and overlap the hero copy below it, at any width.
      className={size === "big" ? "h-[clamp(5rem,3rem+4.5vw,11.5rem)] w-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.55)]" : "h-9 sm:h-10 w-auto mobile-logo-mark"}
    />
  );

  // Sits beside the capsule, not stacked above or overlapping it - the
  // capsule (nav links, CTA) starts to the right of the logo, at the same
  // vertical level, rather than the logo sitting on top of/before the bar
  // begins underneath it. Both are siblings in the same flex row below
  // (see the isHome return), so this is just the logo's own content.
  const logoBig = (
    <Link to="/" className="flex-shrink-0">
      {logoImg("big")}
    </Link>
  );

  const navContent = (
    <div className="max-w-[clamp(80rem,40rem+50vw,120rem)] mx-auto flex items-center gap-4 py-2 px-4 sm:px-6 mobile-nav-inner">
      {/* flex-1 min-w-0: gives this Link exactly whatever width is left
          over after the nav/CTA/menu-button siblings (all flex-shrink-0
          below) take their own natural size - that's the real, bounded
          "available width" FitText measures the wordmark against. Without
          min-w-0 here, a flex item never shrinks below its content's own
          intrinsic width, so a long business name would just push those
          siblings aside (or wrap/overflow) instead of ever being handed a
          real constraint to fit itself into. The row's own `gap-4` is
          subtracted from that leftover space by the browser before FitText
          ever measures it, so even a name long enough to use every last
          pixel it's given still keeps a real gap from the nav/menu-button
          next to it, instead of visually touching it. */}
      <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        <span className="w-2 h-2 rounded-full flex-shrink-0 mobile-brand-dot" style={{ background: CYAN }} />
        {/* The image itself is the only part that ever leaves this slot -
            hidden here (not removed) while the big version floats above,
            via layoutId doing the shrink-and-return once pastHero flips.
            This Link used to space its children with space-x-2/3, which
            works by putting a margin on each child element - but a
            display:contents wrapper (used below so the actual <img>
            counts as a direct flex item once it's back in this row) has
            no box of its own for that margin to apply to, so the margin
            was silently dropped and the docked logo rendered flush
            against the dot. Flex `gap` doesn't have that problem - it
            spaces whatever actually participates in the flex layout,
            display:contents wrapper or not. */}
        <span className={showBigHeroLogo ? "invisible w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" : "contents"}>
          {!showBigHeroLogo && logoImg("small")}
        </span>
        <FitText className="text-sm sm:text-base lg:text-lg font-bold tracking-wider text-white mobile-wordmark">
          {wordmark}
        </FitText>
      </Link>

      {/* Desktop Nav */}
      <nav className="hidden lg:flex items-center space-x-5 xl:space-x-7 flex-shrink-0">
        <Link to="/" className={linkCls}>Home</Link>
        <Link to="/services" className={linkCls}>Services</Link>
        <Link to="/why-us" className={linkCls}>Why Us</Link>
        <Link to="/packages" className={linkCls}>Packages</Link>
        <Link to="/about" className={linkCls}>About</Link>
        <Link to="/contact" className={linkCls}>Contact</Link>

        {/* Areas Dropdown (Desktop hover) */}
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Link to="/areas" className={`flex items-center ${linkCls}`}>
            Areas <ChevronDown size={14} className="ml-1 transition-transform duration-200" />
          </Link>

          {isAreasOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[380px] rounded-2xl p-5 z-50 bg-[#0a0f1a]/95 backdrop-blur-xl border border-white/10 text-white">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-white">Service Areas</h4>
                <p className="text-xs text-white/50">Professional cleaning services across all Sydney suburbs</p>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {navAreas.map((area) => (
                  <Link
                    key={area.slug}
                    to={`/areas/${area.slug}`}
                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition text-white/70 hover:bg-white/10"
                  >
                    <MapPin size={13} style={{ color: CYAN }} />
                    {area.name}
                  </Link>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-white/10">
                <Link to="/areas" className="block text-sm font-medium hover:text-white" style={{ color: CYAN }}>
                  View All Service Areas →
                </Link>
              </div>
            </div>
          )}
        </div>

        <Link to="/blog" className={linkCls}>Blog</Link>
        <Link
          to="/contact"
          className="text-black px-4 py-2 rounded-full font-semibold text-sm hover:bg-white transition-colors whitespace-nowrap"
          style={{ background: CYAN }}
        >
          Get a Free Quote
        </Link>
      </nav>

      {/* Mobile Menu Button */}
      <div className="lg:hidden flex-shrink-0">
        <button onClick={() => setIsOpen(!isOpen)} className="text-white mobile-menu-button" aria-label="Toggle menu">
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </div>
  );

  const mobileMenu = isOpen && (
    <div className="lg:hidden bg-[#02060c]/98 backdrop-blur-xl border-t border-white/10">
      <nav className="flex flex-col space-y-3 px-6 py-6">
        <Link to="/" className={linkCls} onClick={() => setIsOpen(false)}>Home</Link>
        <Link to="/services" className={linkCls} onClick={() => setIsOpen(false)}>Services</Link>
        <Link to="/why-us" className={linkCls} onClick={() => setIsOpen(false)}>Why Us</Link>
        <Link to="/packages" className={linkCls} onClick={() => setIsOpen(false)}>Packages</Link>
        <Link to="/about" className={linkCls} onClick={() => setIsOpen(false)}>About</Link>
        <Link to="/contact" className={linkCls} onClick={() => setIsOpen(false)}>Contact</Link>

        <div className="flex flex-col">
          <button
            onClick={() => setIsAreasOpen(!isAreasOpen)}
            className={`flex justify-between items-center w-full ${linkCls}`}
          >
            Areas
            <ChevronDown size={16} className={`ml-2 transition-transform ${isAreasOpen ? "rotate-180" : ""}`} />
          </button>
          {isAreasOpen && (
            <div className="pl-4 mt-2 flex flex-col space-y-2">
              {navAreas.map((area) => (
                <Link
                  key={area.slug}
                  to={`/areas/${area.slug}`}
                  className="flex items-center gap-2 text-sm text-white/60 hover:text-[#22d3ee]"
                  onClick={() => setIsOpen(false)}
                >
                  <MapPin size={13} className="text-[#22d3ee]" />
                  {area.name}
                </Link>
              ))}
              <Link
                to="/areas"
                className="mt-1 text-sm font-medium text-[#22d3ee]"
                onClick={() => setIsOpen(false)}
              >
                View All Service Areas →
              </Link>
            </div>
          )}
        </div>

        <Link to="/blog" className={linkCls} onClick={() => setIsOpen(false)}>Blog</Link>
        <Link
          to="/contact"
          className="text-center text-black px-4 py-2.5 rounded-full font-semibold"
          style={{ background: CYAN }}
          onClick={() => setIsOpen(false)}
        >
          Get a Free Quote
        </Link>
      </nav>
    </div>
  );

  if (isHome) {
    // Floating capsule nav, inset from the viewport edges, sitting on top
    // of the hero rather than a flush full-width bar.
    return (
      <header className="fixed w-full top-4 z-50 px-4 mobile-floating-header">
        {/* Logo and capsule are siblings in one row - the capsule starts
            to the right of the logo at the same vertical level, rather
            than the logo sitting stacked above/overlapping where the bar
            begins. Only rendered while showBigHeroLogo is true; once
            pastHero flips, this whole slot disappears and the capsule
            below (now alone, flex-1 filling the full row) shows the small
            docked version of the same image via the shared layoutId. */}
        <div className="max-w-[clamp(80rem,40rem+50vw,120rem)] mx-auto flex items-center gap-4 sm:gap-6">
          {showBigHeroLogo && logoBig}
          <div
            // Frosted glass over the hero photo (light blur + faint border,
            // reads as floating glass rather than a solid bar) for as long as
            // the hero photo is actually behind it. Once scrolled past the
            // hero into flat dark page content, switches to the same solid
            // glass pill every other page uses, so it stays legible instead
            // of staying near-invisible over nothing.
            className={`flex-1 min-w-0 rounded-2xl transition-all duration-300 mobile-nav-capsule ${
              pastHero
                ? "bg-[#02060c]/90 backdrop-blur-xl border border-white/10 mobile-nav-solid"
                : "bg-white/[0.06] backdrop-blur-sm border border-white/[0.10] mobile-nav-hero"
            }`}
          >
            {navContent}
          </div>
        </div>

        {isOpen && (
          <div className="max-w-7xl mx-auto mt-2 rounded-2xl bg-[#02060c]/98 backdrop-blur-xl border border-white/10 lg:hidden overflow-hidden">
            {mobileMenu}
          </div>
        )}
      </header>
    );
  }

  // Every other page: same dark palette, but a flush solid bar since there's
  // no hero image underneath for a transparent nav to blend into. No
  // backdrop-blur here - at 95% opacity there's nothing visible behind it
  // left to blur, so the filter would just be wasted GPU work on every
  // scroll frame for zero visual difference.
  return (
    <header className="fixed w-full top-0 z-50 bg-[#02060c]/95 border-b border-white/10">
      {navContent}
      {mobileMenu}
    </header>
  );
};

export default Header;
